/**
 * PLAT-10913 — resolve what an `aiText` control will read.
 *
 * Per epic decision E4 the control never decides which words it covers: it
 * asks `findOverlayRanges`, the same function that draws the transcript
 * underline, so the preview is identical by construction to what the user
 * sees underlined. That function already handles both bindings internally
 * (stable `initial_index` anchor when present, time window otherwise) — the
 * control does not care which path runs.
 *
 * Two scopes (E4):
 *   • `selection` — the words under the graphic's own range.
 *   • `clip`      — everything said in the clip.
 *
 * Removed / cut words are dropped from the resolved text: they render as grey
 * strikethroughs in the transcript and are absent from the rendered clip, so
 * the model must not read them. They stay inside the index range — that is
 * what keeps the range contiguous, see `findOverlayRanges` — we just don't
 * concatenate their values.
 *
 * PLAT-10915 exposes the resolver as a plain function so the generation
 * service, which runs outside React, reads a graphic through exactly the same
 * definition the control previews.
 */
import {
  joinSpeakerTurns,
  joinValues,
} from '@src/services/transcript/speakerTurns';
import { findOverlayRanges } from '@src/utility/transcriptQuoteUtils';
import { useMemo } from 'react';

import type { AITextBaseTextScope } from '../types';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';
import type { TranscriptElement } from '@src/types.ts/transcript';

/**
 * The parts of a transcript element this hook (and `findOverlayRanges`) read.
 * Deliberately structural: the clip editor hands out
 * `ActiveTranscriptTokenWithClipSegmentAttributes` and the transcript view
 * hands out `TranscriptElementWithViewData`, and neither is the other.
 */
export interface AITextTranscriptElement {
  type: string;
  value?: string | null;
  state?: string;
  initial_index?: number;
  ts?: number | null;
  end_ts?: number | null;
  /** Internal `spk_X` label — turns the `clip` scope into a speaker-labelled read. */
  speaker_label?: string;
}

export interface AITextBaseText {
  /** The words this scope resolves to, in transcript order. */
  elements: AITextTranscriptElement[];
  /** Those words joined into readable text. */
  text: string;
  /** Spoken words only — punctuation and whitespace excluded. */
  wordCount: number;
  /** Spoken length in seconds; 0 when the words carry no timings. */
  durationSeconds: number;
  /**
   * True when `selection` scope could not resolve a range — the graphic has no
   * anchor and its time window covers no surviving word (e.g. every word it
   * sat over was cut).
   */
  unresolved: boolean;
}

const EMPTY: AITextBaseText = {
  elements: [],
  text: '',
  wordCount: 0,
  durationSeconds: 0,
  unresolved: true,
};

export interface UseAITextBaseTextParams {
  scope: AITextBaseTextScope;
  /**
   * The timeline marker for the graphic this control belongs to — supplies
   * the anchor / time window `findOverlayRanges` maps from. `null` on a
   * surface that hasn't wired markers; `selection` then resolves to nothing.
   */
  overlay: TimelineMarker | null | undefined;
  /** The clip's transcript elements. */
  transcriptElements: ReadonlyArray<AITextTranscriptElement> | null | undefined;
}

export function useAITextBaseText(
  params: UseAITextBaseTextParams,
): AITextBaseText {
  const { scope, overlay, transcriptElements } = params;
  return useMemo(
    () => resolveAITextBaseText({ scope, overlay, transcriptElements }),
    [scope, overlay, transcriptElements],
  );
}

/**
 * The hook's body, callable outside React. PLAT-10915's generation service
 * assembles its base text through this, so "what this graphic reads" has one
 * definition rather than a preview and a generation that can drift apart.
 */
export function resolveAITextBaseText({
  scope,
  overlay,
  transcriptElements,
}: UseAITextBaseTextParams): AITextBaseText {
  const elements = transcriptElements ?? [];
  if (elements.length === 0) return EMPTY;

  // The clip scope is speaker-labelled (E4) — the graphic may be summarizing an
  // exchange, and the prompt has to know who said what.
  if (scope === 'clip') return summarize(elements, true);

  if (!overlay) return EMPTY;
  // `findOverlayRanges` reads `ts` / `end_ts` / `initial_index` off each
  // element with its own defensive casts, so the structural shape above is
  // all it actually needs.
  const [range] = findOverlayRanges(
    [overlay],
    elements as unknown as TranscriptElement[],
  );
  if (!range) return EMPTY;
  return summarize(elements.slice(range.start, range.end + 1), false);
}

/** Reduce a contiguous run of transcript elements to the readable base text. */
function summarize(
  slice: ReadonlyArray<AITextTranscriptElement>,
  labelSpeakers: boolean,
): AITextBaseText {
  const elements = slice.filter(
    el => el.state !== 'removed' && el.state !== 'cut',
  );
  const words = elements.filter(el => el.type === 'text');
  const text = labelSpeakers
    ? joinSpeakerTurns(elements)
    : joinValues(elements);

  // `ts` is `number` on the type but `null` at runtime for words that lost
  // their timing (see CLAUDE.md "Deleted Element Timestamps"); read defensively.
  const start = words.find(el => typeof el.ts === 'number')?.ts ?? 0;
  const end =
    [...words].reverse().find(el => typeof el.end_ts === 'number')?.end_ts ?? 0;

  return {
    elements,
    text,
    wordCount: words.length,
    durationSeconds: Math.max(0, end - start),
    unresolved: elements.length === 0,
  };
}
